import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft, ArrowRight } from 'lucide-react';
import { getAdoArticleBySlug, getAdoPrevNext } from '../data/adoArticles';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

/* ── Imagen con fallback a placeholder ───────────────────── */
const ImagePlaceholder = ({ path, caption, source }) => {
  const [error, setError] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  return (
    <figure className="my-10 sm:my-12">
      {path && !error ? (
        <div className="rounded-xl overflow-hidden bg-[#FEFBF4]">
          <img
            src={path}
            alt={caption}
            className={`w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
          {!loaded && (
            <div className="aspect-video flex items-center justify-center">
              <span className="text-[#C17B00]/40 text-xs">cargando…</span>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-[#C17B00]/40 rounded-xl p-8 sm:p-10 bg-[#FEFBF4]">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-dashed border-[#C17B00]/35 flex items-center justify-center flex-shrink-0">
              <span className="text-[#C17B00]/50 text-[9px] font-bold tracking-[0.18em] uppercase">img</span>
            </div>
            <p className="text-[13px] sm:text-[14px] text-[#C17B00]/70 font-medium leading-relaxed max-w-md">
              {caption}
            </p>
          </div>
        </div>
      )}
      {(caption || source) && (
        <figcaption className="text-[12px] sm:text-[13px] text-[#8793A3] italic mt-3 text-center leading-relaxed px-2">
          {caption && <span>{caption}</span>}
          {caption && source && <span> — </span>}
          {source && <span>{source}</span>}
        </figcaption>
      )}
    </figure>
  );
};

/* ── Sección de referencia bibliográfica ─────────────────── */
const ReferenceNote = ({ label, text, citation }) => (
  <div className="border-t border-[#E5E9EF] mt-14 pt-7">
    <p className="text-[11px] font-bold tracking-[0.18em] text-[#8793A3] uppercase mb-2">
      {label}
    </p>
    <p className="text-[13px] sm:text-[14px] text-[#5F5E5A] italic leading-relaxed mb-3">
      {text}
    </p>
    {citation && (
      <p className="text-[13px] sm:text-[14px] text-[#5F5E5A] font-medium pl-4 border-l-2 border-[#E5E9EF] leading-relaxed">
        {citation}
      </p>
    )}
  </div>
);

/* ── Renderizador de secciones ───────────────────────────── */
const ArticleSection = ({ section, idx }) => {
  switch (section.type) {
    case 'lede':
      return (
        <p
          key={idx}
          className="text-[18px] sm:text-[19px] text-[#1A1A1A] leading-[1.78] mb-7 font-normal"
        >
          {section.content}
        </p>
      );

    case 'body':
      return (
        <p
          key={idx}
          className="text-[17px] sm:text-[18px] text-[#1A1A1A] leading-[1.75] mb-5"
        >
          {section.content}
        </p>
      );

    case 'h2':
      return (
        <h2
          key={idx}
          className="text-[20px] sm:text-[22px] font-bold text-[#0F2844] mt-12 mb-4 leading-snug"
        >
          {section.content}
        </h2>
      );

    case 'pullquote':
      return (
        <blockquote
          key={idx}
          className="border-l-[3px] border-[#534AB7] pl-5 sm:pl-6 my-9 sm:my-10"
        >
          <p className="text-[17px] sm:text-[18px] text-[#534AB7] italic leading-[1.75]">
            {section.content}
          </p>
        </blockquote>
      );

    case 'image':
      return (
        <ImagePlaceholder
          key={idx}
          path={section.path}
          caption={section.caption}
          source={section.source}
        />
      );

    case 'reference':
      return (
        <ReferenceNote
          key={idx}
          label={section.label}
          text={section.text}
          citation={section.citation}
        />
      );

    default:
      return null;
  }
};

/* ── Componente principal ─────────────────────────────────── */
const ColeccionADOArticle = () => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();

  const article = getAdoArticleBySlug(articleSlug);
  const { prev, next } = article
    ? getAdoPrevNext(articleSlug)
    : { prev: null, next: null };

  useEffect(() => {
    if (!article) {
      navigate('/colecciones/ado', { replace: true });
    }
  }, [article, navigate]);

  if (!article) return null;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`ADO·${article.numero}: ${article.titulo} | Edutech Pulse Lab`}
        description={article.deck}
        canonicalUrl={`/colecciones/ado/${article.slug}`}
      />
      <Navbar />

      {/* ── Cabecera del artículo ───────────────────────────── */}
      <header className="bg-[#0c203b] relative overflow-hidden">
        {/* Línea de acento dorado — sistema visual ADO */}
        <div className="absolute top-0 left-0 w-[40%] h-[3px] bg-[#C4933A]" />

        {/* Patrón de fondo muy sutil */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <svg
            viewBox="0 0 800 280"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
            preserveAspectRatio="xMaxYMid slice"
          >
            <line x1="700" y1="0" x2="700" y2="280" stroke="#8DB8D4" strokeWidth="0.5" strokeOpacity="0.06" />
            <line x1="0" y1="140" x2="800" y2="140" stroke="#8DB8D4" strokeWidth="0.5" strokeOpacity="0.05" />
            <rect x="640" y="40" width="140" height="200" stroke="#C4933A" strokeWidth="0.5" strokeOpacity="0.08" fill="none" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-8 pb-10 sm:pt-10 sm:pb-12 relative">
          <div className="flex flex-col lg:flex-row lg:items-start lg:gap-14 xl:gap-20">

            {/* ── Columna izquierda: texto ── */}
            <div className="flex-1 min-w-0">
              {/* Breadcrumb */}
              <nav
                className="flex items-center gap-1.5 text-[11px] sm:text-[12px] text-white/35 mb-7 flex-wrap"
                aria-label="Navegación"
              >
                <Link to="/" className="hover:text-white/60 transition-colors" aria-label="Inicio">
                  <Home className="w-3 h-3" />
                </Link>
                <ChevronRight className="w-3 h-3 flex-shrink-0 text-white/20" />
                <Link to="/colecciones" className="hover:text-white/60 transition-colors">
                  Colecciones
                </Link>
                <ChevronRight className="w-3 h-3 flex-shrink-0 text-white/20" />
                <Link to="/colecciones/ado" className="hover:text-white/60 transition-colors">
                  ADO
                </Link>
                <ChevronRight className="w-3 h-3 flex-shrink-0 text-white/20" />
                <span className="text-white/20">ADO·{article.numero}</span>
              </nav>

              {/* Etapa de lectura */}
              <div className="flex items-center gap-2.5 mb-5">
                <span className="text-[10px] font-bold tracking-[0.2em] text-amber-400/65 uppercase">
                  Etapa {article.stage.num}
                </span>
                <span className="text-white/15">·</span>
                <span className="text-[10px] font-semibold tracking-[0.14em] text-white/35 uppercase">
                  {article.stage.label}
                </span>
              </div>

              {/* Número de artículo */}
              <p className="text-[12px] font-medium text-amber-400/45 tracking-[0.22em] mb-3 font-mono uppercase">
                ADO·{article.numero}
              </p>

              {/* Título */}
              <h1 className="text-[26px] sm:text-[34px] lg:text-[36px] font-bold text-white leading-[1.13] mb-5 tracking-tight max-w-[560px]">
                {article.titulo}
              </h1>

              {/* Deck / subtítulo */}
              <p className="text-[15px] sm:text-[17px] text-white/55 italic leading-[1.65] mb-7 max-w-[520px]">
                {article.deck}
              </p>

              {/* Metadatos */}
              <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[12px] sm:text-[13px] text-white/35">
                <span className="font-medium text-white/50">David Prudencio</span>
                <span className="text-white/15">·</span>
                <span>{article.date}</span>
                <span className="text-white/15">·</span>
                <span>{article.readTime} de lectura</span>
              </div>
            </div>

            {/* ── Columna derecha: imagen de portada (solo desktop) ── */}
            {article.headerImage && (
              <div className="hidden lg:flex flex-shrink-0 w-[300px] xl:w-[360px] items-center self-stretch pt-10">
                <div className="w-full rounded-xl overflow-hidden opacity-90 ring-1 ring-white/8">
                  <img
                    src={article.headerImage}
                    alt={`Portada ADO·${article.numero} — ${article.titulo}`}
                    className="w-full block"
                    loading="eager"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Cuerpo del artículo ─────────────────────────────── */}
      <article>
        <div className="max-w-[720px] mx-auto px-5 sm:px-8 pt-12 pb-10 sm:pt-14 sm:pb-14">
          {article.sections.map((section, idx) => (
            <ArticleSection key={idx} section={section} idx={idx} />
          ))}
        </div>
      </article>

      {/* ── Navegación anterior / siguiente ─────────────────── */}
      <nav
        className="border-t border-slate-100 bg-slate-50/60"
        aria-label="Navegación entre artículos"
      >
        <div className="max-w-[720px] mx-auto px-5 sm:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-0 sm:justify-between">
            {/* Anterior */}
            {prev ? (
              <Link
                to={`/colecciones/ado/${prev.slug}`}
                className="group flex items-start gap-3 sm:max-w-[46%]"
              >
                <ArrowLeft className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0 group-hover:-translate-x-0.5 transition-transform duration-200" />
                <div>
                  <p className="text-[10px] font-bold tracking-[0.18em] text-slate-400 uppercase mb-1.5">
                    Artículo anterior
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-medium text-[#0F2844] group-hover:text-slate-500 transition-colors leading-snug">
                    ADO·{prev.numero} — {prev.titulo}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {/* Siguiente */}
            {next ? (
              <Link
                to={`/colecciones/ado/${next.slug}`}
                className="group flex items-start gap-3 sm:max-w-[46%] sm:text-right sm:flex-row-reverse"
              >
                <ArrowRight className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform duration-200" />
                <div>
                  <p className="text-[10px] font-bold tracking-[0.18em] text-slate-400 uppercase mb-1.5">
                    Siguiente artículo
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-medium text-[#0F2844] group-hover:text-slate-500 transition-colors leading-snug">
                    ADO·{next.numero} — {next.titulo}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </nav>

      {/* ── CTA — volver a la colección ──────────────────────── */}
      <section className="bg-[#0c203b]">
        <div className="max-w-[720px] mx-auto px-5 sm:px-8 py-10 sm:py-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] text-white/25 uppercase mb-2">
              Serie ADO
            </p>
            <p className="text-[14px] sm:text-[15px] text-white/60 leading-snug">
              Arquitectura de Decisión Organizacional · 7 artículos
            </p>
          </div>
          <Link
            to="/colecciones/ado"
            className="group inline-flex items-center gap-2 text-[13px] sm:text-[14px] font-semibold text-white border border-white/20 hover:border-white/50 px-5 py-2.5 rounded-lg transition-all duration-200 flex-shrink-0 w-full sm:w-auto justify-center sm:justify-start"
          >
            Ver colección completa
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ColeccionADOArticle;
